import React from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useAuth0 } from "@auth0/auth0-react";
import Button from "components/Button/Regular";
import Input from "components/Input/Input";
import Select from "components/Input/Select";
import vgToast from "utils/toast";
import { readOrganisation, updateOrganisationLocation } from "services/clinics.service";
import { Place, placeAt, searchPlaces } from "services/places.service";
import { UNKNOWN_COUNTRY, countryCode, countryName, countryOptions, countryValue } from "constants/countries";

type Pin = { latitude: number; longitude: number };

// Europe, when there is no pin yet.
const NO_PIN_VIEW: { center: L.LatLngTuple; zoom: number } = { center: [50, 10], zoom: 4 };
const PIN_ZOOM = 16;

// Drawn rather than Leaflet's default marker, whose images the bundler does not find.
const PIN_ICON = L.divIcon({
  className: "",
  html:
    '<svg width="28" height="40" viewBox="0 0 28 40" xmlns="http://www.w3.org/2000/svg">' +
    '<path d="M14 0C6.3 0 0 6.3 0 14c0 10.5 14 26 14 26s14-15.5 14-26C28 6.3 21.7 0 14 0z" fill="#1d4ed8"/>' +
    '<circle cx="14" cy="14" r="5" fill="white"/></svg>',
  iconSize: [28, 40],
  iconAnchor: [14, 40],
});

/**
 * Where a provider is: a pin on an OpenStreetMap map, found by searching or placed by
 * clicking and dragging, the address there, and its country. The marketplace finds providers
 * by the pin, and Stripe onboarding needs the country.
 *
 * The country follows the pin (OpenStreetMap's for the place, and Core's own borders when
 * that has none) unless one is chosen here, which overrides it.
 */
const LocationForm = ({ orgId }: { orgId: number }) => {
  const { getAccessTokenSilently } = useAuth0();
  const mapElement = React.useRef<HTMLDivElement | null>(null);
  const map = React.useRef<L.Map | null>(null);
  const marker = React.useRef<L.Marker | null>(null);

  const [loaded, setLoaded] = React.useState(false);
  const [pin, setPin] = React.useState<Pin | null>(null);
  const [address, setAddress] = React.useState("");
  const [country, setCountry] = React.useState<number>(UNKNOWN_COUNTRY);
  const [pinCountry, setPinCountry] = React.useState("");
  const [query, setQuery] = React.useState("");
  const [results, setResults] = React.useState<Place[]>([]);
  const [saving, setSaving] = React.useState(false);
  const options = React.useMemo(countryOptions, []);

  // A pin moved by hand: name the place under it, and let the country follow it again.
  const movePinTo = React.useCallback(async (latitude: number, longitude: number) => {
    setPin({ latitude, longitude });
    setCountry(UNKNOWN_COUNTRY);
    setPinCountry("");
    try {
      const place = await placeAt(latitude, longitude);
      if (place) {
        setAddress(place.address);
        setPinCountry(place.countryCode);
      }
    } catch {
      // The pin is what counts; the address can be typed.
    }
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      const token = await getAccessTokenSilently();
      const { data } = await readOrganisation(token, orgId);
      if (cancelled) return;
      // An organisation with no address was never placed: its latitude and longitude are
      // only Core's defaults.
      if (data?.address && data.latitude != null && data.longitude != null) {
        setPin({ latitude: data.latitude, longitude: data.longitude });
        setAddress(data.address);
        setCountry(data.country ?? UNKNOWN_COUNTRY);
      }
      setLoaded(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId, getAccessTokenSilently]);

  // The map, once.
  React.useEffect(() => {
    if (!loaded || !mapElement.current || map.current) return;
    const created = L.map(mapElement.current).setView(NO_PIN_VIEW.center, NO_PIN_VIEW.zoom);
    L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(created);
    created.on("click", (event: L.LeafletMouseEvent) => movePinTo(event.latlng.lat, event.latlng.lng));
    map.current = created;
    return () => {
      created.remove();
      map.current = null;
      marker.current = null;
    };
  }, [loaded, movePinTo]);

  // The marker follows the pin.
  React.useEffect(() => {
    const current = map.current;
    if (!current || !pin) return;
    const at: L.LatLngTuple = [pin.latitude, pin.longitude];
    if (!marker.current) {
      marker.current = L.marker(at, { draggable: true, icon: PIN_ICON }).addTo(current);
      marker.current.on("dragend", () => {
        const moved = marker.current!.getLatLng();
        movePinTo(moved.lat, moved.lng);
      });
      current.setView(at, PIN_ZOOM);
    } else {
      marker.current.setLatLng(at);
      if (!current.getBounds().contains(at)) current.setView(at, PIN_ZOOM);
    }
  }, [pin, loaded, movePinTo]);

  // Suggestions as the search is typed.
  React.useEffect(() => {
    const text = query.trim();
    if (text.length < 3) {
      setResults([]);
      return;
    }
    const abort = new AbortController();
    const timer = window.setTimeout(async () => {
      try {
        setResults(await searchPlaces(text, abort.signal));
      } catch {
        if (!abort.signal.aborted) setResults([]);
      }
    }, 300);
    return () => {
      window.clearTimeout(timer);
      abort.abort();
    };
  }, [query]);

  const choose = (place: Place) => {
    setPin({ latitude: place.latitude, longitude: place.longitude });
    setAddress(place.address);
    setCountry(UNKNOWN_COUNTRY);
    setPinCountry(place.countryCode);
    setQuery("");
    setResults([]);
    map.current?.setView([place.latitude, place.longitude], PIN_ZOOM);
  };

  const save = async () => {
    if (!pin) {
      vgToast.error("Find your address or click the map to place the pin.");
      return;
    }
    if (!address.trim()) {
      vgToast.error("Enter the address.");
      return;
    }
    setSaving(true);
    const token = await getAccessTokenSilently();
    // The pin's country as OpenStreetMap names it, unless one was chosen; with neither,
    // Core finds it from its own borders.
    const sentCountry = country !== UNKNOWN_COUNTRY ? country : countryValue(pinCountry);
    const { error } = await updateOrganisationLocation(token, {
      orgId,
      latitude: pin.latitude,
      longitude: pin.longitude,
      address: address.trim(),
      country: sentCountry,
    });
    setSaving(false);
    if (error) {
      vgToast.error(
        sentCountry === UNKNOWN_COUNTRY
          ? "The pin is not in a country Funely can tell. Choose the country and save again."
          : error.message
      );
      return;
    }
    if (sentCountry !== UNKNOWN_COUNTRY) setCountry(sentCountry);
    vgToast.success("Location saved.");
  };

  const followsPin = country === UNKNOWN_COUNTRY;
  const pinCountryLabel = pinCountry ? countryName(pinCountry) : "found from the pin";

  return (
    <div className="px-5 py-5 flex flex-col gap-5">
      <div className="relative">
        <Input
          label="Find the address"
          placeholder="Start typing the address or place name"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        {results.length > 0 && (
          <ul className="absolute z-[1000] mt-1 w-full rounded-md border border-slate-200 bg-white shadow-lg">
            {results.map((place, index) => (
              <li key={index}>
                <button
                  type="button"
                  onClick={() => choose(place)}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50"
                >
                  {place.address}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        {/* z-0 keeps Leaflet's own layers (its zoom buttons are z-index 1000) inside the map,
            under the address suggestions. */}
        <div ref={mapElement} className="relative z-0 h-80 w-full rounded-md border border-slate-200" />
        <p className="mt-1.5 text-xs text-slate-500">
          Click the map to place the pin, or drag it to the exact spot. Families find you by the pin.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 gap-5">
        <Input label="Address" value={address} onChange={(event) => setAddress(event.target.value)} />
        <Select
          label="Country"
          value={country}
          onChange={(event) => setCountry(Number(event.target.value))}
          options={[{ title: `From the pin (${pinCountryLabel})`, value: UNKNOWN_COUNTRY }, ...options]}
        />
      </div>
      {!followsPin && pinCountry && countryCode(country) !== pinCountry && (
        <p className="-mt-3 text-xs text-slate-500">
          The pin is in {countryName(pinCountry)}; {countryName(countryCode(country))} is used instead.
        </p>
      )}

      <div className="flex justify-end pt-4 border-t border-slate-100">
        <Button color="Primary" type="button" onClick={save} disabled={saving || !loaded}>
          <span>{saving ? "Saving…" : "Save location"}</span>
        </Button>
      </div>
    </div>
  );
};

export default LocationForm;
